import {
  invalidateAdminCompanyTreeQueryData,
  useGetApiAdminJobStatus,
  usePostApiAdminCompanyTreeSettingsClearSyncFields,
  usePostAdminTriggersPackages,
  usePostApiAdminCompanyTreeSettingsBulkDelete,
} from '@sit/client-shared';
import { skipToken, useQueryClient } from '@tanstack/react-query';
import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button/__next__';
import { Checkbox } from 'carbon-react/lib/components/checkbox';
import Dialog from 'carbon-react/lib/components/dialog';
import Divider from 'carbon-react/lib/components/divider';
import {
  FlatTable,
  FlatTableBody,
  FlatTableCell,
  FlatTableCheckbox,
  FlatTableHead,
  FlatTableHeader,
  FlatTableRow,
} from 'carbon-react/lib/components/flat-table';
import { Tile } from 'carbon-react/lib/components/tile';
import Typography from 'carbon-react/lib/components/typography';
import { useCallback, useEffect, useState } from 'react';
import {
  companySyncFieldOptions,
  type CompanySyncField,
} from '../../helpers/company-sync-fields';
import toastr from '../../helpers/toastr';

interface BulkActionsProps {
  selectedCompanies: {
    id: number;
    externalId: string;
    syncEnd: string | null;
    syncStart: string | null;
    lastLoginAttempt?: string | null;
    tenantType: string | null;
  }[];
}

export function BulkActions({ selectedCompanies }: BulkActionsProps) {
  const companyTreeSettingsIds = selectedCompanies.map((c) => c.id);
  const disabled = companyTreeSettingsIds.length === 0;
  const queryClient = useQueryClient();
  const [jobId, setJobId] = useState<string | undefined>();
  const [deleteJobId, setDeleteJobId] = useState<string | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clearSyncFieldsDialogOpen, setClearSyncFieldsDialogOpen] =
    useState(false);
  const [deleteConfirmations, setDeleteConfirmations] = useState<
    Record<number, boolean>
  >({});
  const [clearSyncFields, setClearSyncFields] = useState<CompanySyncField[]>([
    'lastSettingsSync',
  ]);
  useGetApiAdminJobStatus(
    jobId
      ? {
          queryParams: {
            jobId,
            queueName: 'update-triggers-packages',
          },
          requestConfig: {
            skipToast: true,
          },
        }
      : skipToken,
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchInterval: 2_000,
      retry: (failureCount, error) => {
        if (error?.status === 404) {
          toastr.success('Update company triggers package job complete');
          setJobId(undefined);
          void invalidateAdminCompanyTreeQueryData(queryClient);
          return false;
        }
        if (!(error instanceof Error)) {
          return false;
        }
        if (error.message?.includes('Too many requests') || failureCount < 5) {
          return true;
        }
        toastr.error(error.message);
        return false;
      },
    },
  );
  const { mutate: updateTriggersPackages } = usePostAdminTriggersPackages();

  useGetApiAdminJobStatus(
    deleteJobId
      ? {
          queryParams: {
            jobId: deleteJobId,
            queueName: 'delete-company-tree',
          },
          requestConfig: {
            skipToast: true,
          },
        }
      : skipToken,
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchInterval: 2_000,
      retry: (failureCount, error) => {
        if (error?.status === 404) {
          toastr.success('Bulk delete job complete');
          setDeleteJobId(undefined);
          void invalidateAdminCompanyTreeQueryData(queryClient);
          return false;
        }
        if (!(error instanceof Error)) {
          return false;
        }
        if (error.message?.includes('Too many requests') || failureCount < 5) {
          return true;
        }
        toastr.error(error.message);
        return false;
      },
    },
  );

  const { mutate: bulkDeleteCompanies, isPending: isBulkDeletePending } =
    usePostApiAdminCompanyTreeSettingsBulkDelete();
  const { mutate: clearCompanyTreeSettingsSyncFields, isPending: isClearing } =
    usePostApiAdminCompanyTreeSettingsClearSyncFields();

  const startPackageUpdates = useCallback(() => {
    updateTriggersPackages(
      {
        body: {
          companyTreeSettingsIds,
        },
      },
      {
        onSuccess: (data) => {
          setJobId(data.jobId);
          toastr.info('Triggers package update started.');
        },
        onError: (error) => {
          toastr.error(error.message);
        },
      },
    );
  }, [companyTreeSettingsIds, updateTriggersPackages]);

  const startBulkDelete = useCallback(() => {
    bulkDeleteCompanies(
      {
        body: {
          companyTreeSettingsIds,
        },
      },
      {
        onSuccess: (data) => {
          setDeleteJobId(data.jobId);
          setDeleteDialogOpen(false);
          toastr.info('Bulk delete scheduled.');
        },
        onError: (error) => {
          const message =
            error instanceof Error ? error.message : 'Bulk delete failed';
          toastr.error(message);
        },
      },
    );
  }, [bulkDeleteCompanies, companyTreeSettingsIds]);

  const startClearSyncFields = useCallback(() => {
    clearCompanyTreeSettingsSyncFields(
      {
        body: {
          companyTreeSettingsIds,
          fields: clearSyncFields,
        },
      },
      {
        onSuccess: (data) => {
          setClearSyncFieldsDialogOpen(false);
          void invalidateAdminCompanyTreeQueryData(queryClient);
          toastr.success(
            `Cleared ${data.fieldsCleared.length} sync ${data.fieldsCleared.length === 1 ? 'field' : 'fields'} across ${data.companyTreeSettingsIds.length} company ${data.companyTreeSettingsIds.length === 1 ? 'tree' : 'trees'}.`,
          );
        },
        onError: (error) => {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to clear sync fields';
          toastr.error(message);
        },
      },
    );
  }, [
    clearCompanyTreeSettingsSyncFields,
    clearSyncFields,
    companyTreeSettingsIds,
    queryClient,
  ]);

  const companyCount = companyTreeSettingsIds.length;
  const allCompaniesConfirmed =
    companyCount > 0 &&
    companyTreeSettingsIds.every((id) => deleteConfirmations[id]);

  useEffect(() => {
    if (!deleteDialogOpen) return;
    const confirmations = companyTreeSettingsIds.reduce(
      (acc, id) => {
        acc[id] = false;
        return acc;
      },
      {} as Record<number, boolean>,
    );
    setDeleteConfirmations(confirmations);
  }, [deleteDialogOpen, companyTreeSettingsIds]);

  useEffect(() => {
    if (!clearSyncFieldsDialogOpen) return;
    setClearSyncFields(['lastSettingsSync']);
  }, [clearSyncFieldsDialogOpen]);

  return (
    <Box pt={2}>
      <Tile orientation="vertical">
        <Typography variant="h3">Bulk actions</Typography>
        <Divider type="horizontal" mt={2} mb={2} />
        <Box display="flex" flexDirection="row" gap={2}>
          <Button
            disabled={disabled}
            variantType="secondary"
            onClick={startPackageUpdates}
          >
            Update trigger package
          </Button>
          <Button
            disabled={disabled || !!deleteJobId}
            variantType="secondary"
            variant="destructive"
            onClick={() => setDeleteDialogOpen(true)}
          >
            Bulk delete companies
          </Button>
          <Button
            disabled={disabled}
            variantType="secondary"
            onClick={() => setClearSyncFieldsDialogOpen(true)}
          >
            Clear sync fields
          </Button>
        </Box>
      </Tile>
      <Dialog
        open={clearSyncFieldsDialogOpen}
        title="Clear sync fields"
        onCancel={() => setClearSyncFieldsDialogOpen(false)}
      >
        <Typography>
          Select which sync timestamps to clear for the {companyCount}{' '}
          {companyCount === 1
            ? 'selected company tree'
            : 'selected company trees'}
          .
        </Typography>
        <Box mt={3} display="flex" flexDirection="column" gap={2}>
          {companySyncFieldOptions.map((option) => (
            <Checkbox
              key={option.value}
              label={`${option.label} — ${option.description}`}
              checked={clearSyncFields.includes(option.value)}
              onChange={(ev) => {
                setClearSyncFields((current) =>
                  ev.target.checked
                    ? [...current, option.value]
                    : current.filter((field) => field !== option.value),
                );
              }}
            />
          ))}
        </Box>
        <Box mt={3} maxHeight="240px" overflowY="auto">
          <FlatTable colorTheme="transparent-white" hasStickyHead>
            <FlatTableHead>
              <FlatTableRow>
                <FlatTableHeader>Intacct Company ID</FlatTableHeader>
                <FlatTableHeader>Tenant Type</FlatTableHeader>
                <FlatTableHeader>Last Synced</FlatTableHeader>
                <FlatTableHeader>Last Login</FlatTableHeader>
                <FlatTableHeader>Company Tree ID</FlatTableHeader>
              </FlatTableRow>
            </FlatTableHead>
            <FlatTableBody>
              {selectedCompanies.map((company) => {
                const lastSync = company.syncEnd ?? company.syncStart ?? '—';
                const lastLogin = company.lastLoginAttempt ?? '—';
                const tenantType = company.tenantType ?? '—';

                return (
                  <FlatTableRow key={company.id}>
                    <FlatTableCell>{company.externalId}</FlatTableCell>
                    <FlatTableCell>{tenantType}</FlatTableCell>
                    <FlatTableCell>{lastSync}</FlatTableCell>
                    <FlatTableCell>{lastLogin}</FlatTableCell>
                    <FlatTableCell>{company.id}</FlatTableCell>
                  </FlatTableRow>
                );
              })}
            </FlatTableBody>
          </FlatTable>
        </Box>
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variantType="secondary"
            onClick={() => setClearSyncFieldsDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={startClearSyncFields}
            disabled={isClearing || clearSyncFields.length === 0}
          >
            Clear selected fields
          </Button>
        </Box>
      </Dialog>
      <Dialog
        open={deleteDialogOpen}
        title="Bulk delete companies"
        onCancel={() => setDeleteDialogOpen(false)}
      >
        <Typography>
          This will permanently delete {companyCount}{' '}
          {companyCount === 1 ? 'company' : 'companies'} and all associated
          data. This action cannot be undone.
        </Typography>
        <Box mt={3} maxHeight="240px" overflowY="auto">
          <FlatTable colorTheme="transparent-white" hasStickyHead>
            <FlatTableHead>
              <FlatTableRow>
                <FlatTableHeader>Confirm</FlatTableHeader>
                <FlatTableHeader>Intacct Company ID</FlatTableHeader>
                <FlatTableHeader>Tenant Type</FlatTableHeader>
                <FlatTableHeader>Last Synced</FlatTableHeader>
                <FlatTableHeader>Last Login</FlatTableHeader>
                <FlatTableHeader>Company Tree ID</FlatTableHeader>
              </FlatTableRow>
            </FlatTableHead>
            <FlatTableBody>
              {selectedCompanies.map((company) => {
                const lastSync = company.syncEnd ?? company.syncStart ?? '—';
                const lastLogin = company.lastLoginAttempt ?? '—';
                const tenantType = company.tenantType ?? '—';

                return (
                  <FlatTableRow key={company.id}>
                    <FlatTableCheckbox
                      checked={!!deleteConfirmations[company.id]}
                      onChange={(ev) => {
                        setDeleteConfirmations((current) => ({
                          ...current,
                          [company.id]: ev.target.checked,
                        }));
                      }}
                    />
                    <FlatTableCell>{company.externalId}</FlatTableCell>
                    <FlatTableCell>{tenantType}</FlatTableCell>
                    <FlatTableCell>{lastSync}</FlatTableCell>
                    <FlatTableCell>{lastLogin}</FlatTableCell>
                    <FlatTableCell>{company.id}</FlatTableCell>
                  </FlatTableRow>
                );
              })}
            </FlatTableBody>
          </FlatTable>
        </Box>
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variantType="secondary"
            onClick={() => setDeleteDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={startBulkDelete}
            disabled={isBulkDeletePending || !allCompaniesConfirmed}
          >
            Delete
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
}
